import React, { useEffect, useState } from "react";
import { Download, Smartphone, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "simpel_pwa_dismissed_at";
const DISMISS_DAYS = 7;

export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Detect if already running in standalone (installed)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    // Check dismiss cache
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const t = parseInt(raw, 10);
        if (!isNaN(t) && Date.now() - t < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
          return; // still within dismiss window
        }
      }
    } catch {}

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => setInstalled(true);
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setInstalled(true);
      } else {
        dismiss();
      }
    } finally {
      setInstalling(false);
      setDeferredPrompt(null);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  if (installed || !visible || !deferredPrompt) return null;

  return (
    <div className="relative overflow-hidden rounded-lg border border-primary/30 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/5 p-4 mb-6" data-testid="pwa-install-banner">
      <div className="absolute inset-0 papua-motif opacity-40 pointer-events-none" />
      <div className="relative flex items-start gap-4">
        <div className="hidden sm:flex h-12 w-12 rounded-lg bg-primary text-primary-foreground items-center justify-center shrink-0">
          <Smartphone className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-[0.65rem] tracking-widest uppercase text-secondary font-bold mb-1">
            <Sparkles className="h-3 w-3" /> Pasang Aplikasi
          </div>
          <div className="font-heading font-bold text-base text-primary">Buka SIMPEL seperti aplikasi native</div>
          <div className="text-xs text-muted-foreground mt-1">Pasang di layar utama HP atau desktop — akses lebih cepat, tanpa buka browser, dan bisa dipakai saat sinyal lemah.</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={install} disabled={installing} className="rounded-full" data-testid="btn-pwa-install">
            <Download className="h-3.5 w-3.5 mr-1.5" /> {installing ? "Memasang…" : "Pasang"}
          </Button>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Tutup"
            data-testid="btn-pwa-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
